import type { ProjectType } from "../types/index.js";

/** Sanity bounds for a project type's typical construction estimate, in whole CAD dollars. */
export interface ProjectThresholds {
  minReasonable: number;
  maxReasonable: number;
}

/**
 * Configurable, project-specific sanity thresholds. These are
 * deliberately generous (wide enough to admit a real high-end or
 * bare-minimum project) - their job is to catch a calculation gone
 * obviously wrong (a unit-conversion bug, a missing multiplier, a
 * quantity typo), not to second-guess every unusual-but-real estimate.
 * Same disclosure as the rest of the sample dataset: these are
 * administrator-supplied planning bounds, not derived from a market
 * study - see packages/pricing-data/data/README.md for the equivalent
 * disclosure on pricing figures.
 */
export const DEFAULT_PROJECT_THRESHOLDS: Partial<Record<ProjectType, ProjectThresholds>> = {
  kitchen: { minReasonable: 5_000, maxReasonable: 250_000 },
  bathroom: { minReasonable: 3_000, maxReasonable: 150_000 },
  // Higher than plain bathroom - an ensuite is typically larger and more
  // premium-finished (double vanity, freestanding tub, custom shower are
  // all real ensuite-tier selections that would false-flag against
  // bathroom's own ceiling).
  ensuite: { minReasonable: 4_000, maxReasonable: 220_000 },
  basement: { minReasonable: 8_000, maxReasonable: 300_000 },
  basement_suite: { minReasonable: 15_000, maxReasonable: 350_000 },
  whole_home_renovation: { minReasonable: 25_000, maxReasonable: 1_000_000 },
  addition: { minReasonable: 20_000, maxReasonable: 750_000 },

  // Phase 11 - Deck & Patio Estimator
  deck_patio: { minReasonable: 3_000, maxReasonable: 200_000 },

  // Phase 13A - Interior Room Estimators
  living_room: { minReasonable: 2_000, maxReasonable: 150_000 },
  family_room: { minReasonable: 2_000, maxReasonable: 150_000 },
  dining_room: { minReasonable: 1_500, maxReasonable: 100_000 },
  bedroom: { minReasonable: 1_500, maxReasonable: 100_000 },
  primary_bedroom: { minReasonable: 2_000, maxReasonable: 150_000 },
  home_office: { minReasonable: 1_500, maxReasonable: 100_000 },
  laundry_room: { minReasonable: 1_500, maxReasonable: 60_000 },
  mudroom: { minReasonable: 1_500, maxReasonable: 60_000 },
  entryway_foyer: { minReasonable: 1_500, maxReasonable: 80_000 },
  hallway: { minReasonable: 500, maxReasonable: 40_000 },

  // Phase 13B - Garage, Attic, Exterior Estimators
  garage: { minReasonable: 2_000, maxReasonable: 150_000 },
  attic: { minReasonable: 1_500, maxReasonable: 150_000 },
  exterior: { minReasonable: 1_500, maxReasonable: 250_000 }
};

/** Generic fallback bounds for a project type with no specific entry above. */
export const GENERIC_PROJECT_THRESHOLDS: ProjectThresholds = { minReasonable: 1_000, maxReasonable: 500_000 };

export interface ThresholdEvaluation {
  withinRange: boolean;
  thresholds: ProjectThresholds;
  reason: string | null;
}

/**
 * Checks a computed typical estimate against configured sanity bounds.
 * Never clamps or alters the number - only reports whether it's outside
 * the expected range and why, so the caller can flag it for manual
 * review rather than silently returning (or silently suppressing) an
 * implausible figure.
 */
export function evaluateProjectThresholds(
  projectType: ProjectType,
  typicalAmount: number,
  config: Partial<Record<ProjectType, ProjectThresholds>> = DEFAULT_PROJECT_THRESHOLDS
): ThresholdEvaluation {
  const thresholds = config[projectType] ?? GENERIC_PROJECT_THRESHOLDS;

  if (typicalAmount < thresholds.minReasonable) {
    return {
      withinRange: false,
      thresholds,
      reason: `Typical estimate of $${typicalAmount.toLocaleString("en-CA")} is below the configured minimum of $${thresholds.minReasonable.toLocaleString("en-CA")} for this project type.`
    };
  }
  if (typicalAmount > thresholds.maxReasonable) {
    return {
      withinRange: false,
      thresholds,
      reason: `Typical estimate of $${typicalAmount.toLocaleString("en-CA")} exceeds the configured maximum of $${thresholds.maxReasonable.toLocaleString("en-CA")} for this project type.`
    };
  }
  return { withinRange: true, thresholds, reason: null };
}
